import { cn, formatNumber } from '@/lib/utils';
import { useTranslation } from '@/i18n';
import type { GameState } from './types';
import { GAME_CONFIG } from './types';

interface GameHUDProps {
  gameState: GameState;
  onPause: () => void;
  onMenu: () => void;
}

export function GameHUD({ gameState, onPause, onMenu }: GameHUDProps) {
  const { score, placedCount, totalParts, missesCount, level } = gameState;
  const { t } = useTranslation();

  return (
    <div className="absolute top-0 left-0 right-0 z-40 bg-bg-deep/95 border-b border-border-dark">
      <div className="flex items-center justify-between px-2 sm:px-4 py-2 sm:py-3">
        {/* Left Stats - responsive layout */}
        <div className="flex items-center gap-2 sm:gap-4 md:gap-6 flex-wrap">
          <StatBox label={t.common.score} value={formatNumber(score)} />
          <StatBox 
            label={t.automationDrop.partsLabel.replace(':', '')} 
            value={`${placedCount}/${totalParts}`}
            highlight
          />
          <StatBox 
            label={t.automationDrop.missesLabel.replace(':', '')} 
            value={`${missesCount}/${GAME_CONFIG.maxAllowedMisses}`}
            danger={missesCount >= GAME_CONFIG.maxAllowedMisses - 2}
          />
          {/* Level - hidden on very small screens */}
          <div className="hidden xs:block">
            <StatBox label="Level" value={(level + 1).toString()} />
          </div>
        </div>

        {/* Right Controls - responsive */}
        <div className="flex items-center gap-1 sm:gap-3">
          <button 
            onClick={onPause}
            className="px-3 sm:px-5 py-1.5 sm:py-2 rounded-full border border-border-light bg-bg-card
                     text-text-secondary text-sm sm:text-base font-semibold hover:bg-border-dark transition-colors"
          >
            <span className="hidden sm:inline">{t.common.pause}</span>
            <span className="sm:hidden">||</span>
          </button>
          <button 
            onClick={onMenu}
            className="px-3 sm:px-5 py-1.5 sm:py-2 rounded-full border border-border-dark bg-bg-card
                     text-text-secondary text-sm sm:text-base font-semibold hover:bg-border-dark transition-colors"
          >
            <span className="hidden sm:inline">{t.common.menu}</span>
            <span className="sm:hidden">☰</span>
          </button>
        </div>
      </div>
    </div>
  );
}

interface StatBoxProps {
  label: string;
  value: string;
  highlight?: boolean;
  danger?: boolean;
}

function StatBox({ label, value, highlight, danger }: StatBoxProps) {
  return (
    <div className="flex items-baseline gap-1 sm:gap-2">
      <span className={cn(
        "text-base sm:text-xl font-bold",
        highlight && "text-accent-gold",
        danger && "text-accent-red",
        !highlight && !danger && "text-text-primary"
      )}>
        {value}
      </span>
      <span className="text-text-muted text-xs sm:text-sm uppercase hidden sm:inline">{label}</span>
    </div>
  );
}
